import type NutrientViewerType from "@nutrient-sdk/viewer";

/**
 * The @nutrient-sdk/viewer JS entry wrapper is ~2 MB gzip — 24% of the main bundle when
 * imported statically, because any statically-reachable import pins it into the entry
 * chunk for every user (ado-281459). All runtime access therefore goes through the
 * dynamic-import wrapper below so Rollup splits the SDK into its own async chunk that
 * only downloads when a viewer actually mounts.
 *
 * Rules for consumers:
 *  - `import type` from "@nutrient-sdk/viewer" freely — types are erased at build time.
 *  - Never `import NutrientViewer from "@nutrient-sdk/viewer"` as a value outside this
 *    file; that re-pins the SDK into the entry chunk.
 *  - Await {@link loadNutrientViewer} before the first SDK use (typically in the load
 *    effect, before calling `NutrientViewer.load`). Code that provably runs after that —
 *    event handlers, imperative-handle methods, helpers operating on a live instance —
 *    can use the {@link NutrientViewer} proxy exactly like the old static import.
 */
let nutrientViewer: typeof NutrientViewerType | null = null;
let nutrientViewerPromise: Promise<typeof NutrientViewerType> | null = null;

export const loadNutrientViewer = (): Promise<typeof NutrientViewerType> => {
    nutrientViewerPromise ??= import("@nutrient-sdk/viewer").then((mod) => {
        nutrientViewer = mod.default;
        return mod.default;
    });
    return nutrientViewerPromise;
};

const getNutrientViewer = (): typeof NutrientViewerType => {
    if (!nutrientViewer) {
        throw new Error(
            "NutrientViewer accessed before loadNutrientViewer() resolved. Await loadNutrientViewer() first.",
        );
    }
    return nutrientViewer;
};

/**
 * Drop-in stand-in for the old `import NutrientViewer from "@nutrient-sdk/viewer"`:
 * property access is forwarded to the loaded SDK module, so existing call sites
 * (`NutrientViewer.ViewState`, `new NutrientViewer.Geometry.Rect(...)`, enums, etc.)
 * keep working unchanged. Throws if a property is read before
 * {@link loadNutrientViewer} has resolved — such a call site runs too early and must
 * await the load (or, for render-time enum reads, use the string mirrors below).
 */
export const NutrientViewer: typeof NutrientViewerType = new Proxy(
    {} as typeof NutrientViewerType,
    {
        get(_target, prop): unknown {
            return Reflect.get(getNutrientViewer(), prop);
        },
    },
);

/**
 * String-literal mirrors of SDK enum values that are read during React render — before
 * the SDK chunk may have loaded, where reading them off the module would throw (or force
 * an eager load). `satisfies` ties each literal to the SDK's own type so drift fails the
 * build. Post-load code should keep using the enums on the loaded module directly.
 */
export const NutrientAutoSaveMode = {
    INTELLIGENT: "INTELLIGENT",
} as const satisfies Partial<typeof NutrientViewerType.AutoSaveMode>;

export const NutrientDocumentComparisonSourceType = {
    USE_OPEN_DOCUMENT: "USE_OPEN_DOCUMENT",
} as const satisfies Partial<typeof NutrientViewerType.DocumentComparisonSourceType>;

// Tracks whether the one-time worker warm-up has been kicked off this session.
let workerPreloadStarted = false;

/**
 * Warms the standalone WASM worker so the first `NutrientViewer.load()` — and any
 * loads that fire while it is still resolving (e.g. the two panes of the plan
 * side-by-side / comparison views) — reuse an already-fetched core instead of each
 * cold-instantiating it. Per the SDK, a `load()` called before the preload resolves
 * simply reuses the in-flight request, so warming once up front benefits every
 * viewer that mounts afterward. Safe to call repeatedly; it only warms once.
 *
 * Also fetches the SDK's JS chunk itself (via loadNutrientViewer), so calling this on
 * a page that will mount a viewer doubles as a chunk prefetch.
 */
export const preloadNutrientWorker = (licenseKey: string | undefined): void => {
    if (workerPreloadStarted || !licenseKey) {
        return;
    }
    workerPreloadStarted = true;
    const baseUrl = `${window.location.protocol}//${window.location.host}${
        import.meta.env.BASE_URL
    }`;
    // preloadWorker only needs the worker/asset coordinates (licenseKey + baseUrl);
    // `container`/`document` are required by the full load config but are unused when
    // preloading, so we cast rather than fabricate them.
    void loadNutrientViewer()
        .then((NutrientViewer) =>
            NutrientViewer.preloadWorker({ licenseKey, baseUrl } as unknown as Parameters<
                typeof NutrientViewer.preloadWorker
            >[0]),
        )
        .catch(() => {
            // Best-effort warm-up: if it fails, the normal load() path still works
            // (just without the head start). Allow a later call to retry.
            workerPreloadStarted = false;
        });
};

/**
 * Safely sets Nutrient breakpoint options to prevent toolbar icons from hiding
 * as the modal gets smaller, allowing users to scroll the toolbar horizontally.
 * Only sets the options if they haven't been set already (to avoid read-only property errors).
 *
 * Must be called after {@link loadNutrientViewer} has resolved (the SDK options are
 * global, so viewers call this in their load path rather than at module scope).
 */
export const configureNutrientBreakpoints = (): void => {
    const NutrientViewer = getNutrientViewer();
    const smToolbarDescriptor = Object.getOwnPropertyDescriptor(
        NutrientViewer.Options,
        "BREAKPOINT_SM_TOOLBAR",
    );
    const mdToolbarDescriptor = Object.getOwnPropertyDescriptor(
        NutrientViewer.Options,
        "BREAKPOINT_MD_TOOLBAR",
    );

    if (!smToolbarDescriptor || smToolbarDescriptor.writable !== false) {
        NutrientViewer.Options.BREAKPOINT_SM_TOOLBAR = 0;
    }
    if (!mdToolbarDescriptor || mdToolbarDescriptor.writable !== false) {
        NutrientViewer.Options.BREAKPOINT_MD_TOOLBAR = 0;
    }
};

/**
 * Strips the wrapping `<div>` that BDS icons render around their `<svg>`, then
 * replaces the SVG's baked-in `width="1em" height="1em"` and generated `id`
 * with the requested pixel dimensions. Without this, the duplicate width/height
 * attributes leave the original `1em` values in force and the requested size
 * is silently ignored.
 */
/**
 * NutrientViewer / PSPDFKit instance methods throw a specific class of errors
 * when called after the underlying document has been unloaded — typically
 * because the user closed the viewer (or it was unmounted) between
 * `const instance = instanceRef.current` and an `await instance.someMethod(...)`
 * further down the same flow. The local handle is still object-valid, but
 * the document is gone, so the SDK throws.
 *
 * Known message variants observed in Sentry:
 *   - "no open document"
 *   - "No document is loaded"
 *   - "This method can not be called since there is no open document. Have you run NutrientViewer.unload()?"
 *
 * Use this predicate to swallow those benign races; re-throw anything else.
 *
 * Fixes BTNET-REACT-B9PN ("No document is loaded") and BTNET-REACT-BA57
 * ("This method can not be called since there is no open document").
 */
export const isPSPDFKitDocumentClosedError = (e: unknown): boolean => {
    const msg = String((e as { message?: string } | null)?.message ?? e);
    return (
        msg.includes("no open document") ||
        msg.includes("No document is loaded") ||
        msg.includes("there is no open document")
    );
};

export const extractBdsSvgIcon = (iconString: string, height: number, width: number) => {
    const svgExtract = /<div.*?>(<svg.*?<\/svg>)<\/div>/;

    return iconString
        .replace(svgExtract, "$1")
        .replace(/\s+(?:width|height|id)="[^"]*"/g, "")
        .replace(/<svg/, `<svg width="${width}" height="${height}"`);
};
